<template>
  <!-- Mobile Only - Slide-up Modal -->
  <div class="md:hidden">
    <!-- Backdrop Overlay -->
    <transition name="backdrop-fade">
      <div
        v-if="showModal"
        class="fixed inset-0 bg-[#2F3033]/30 backdrop-blur-sm z-[60] transition-all duration-300"
        @click="closeModal"
      ></div>
    </transition>

    <!-- Slide-up Modal -->
    <transition name="slide-up">
      <div
        v-if="showModal"
        class="flex flex-col fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-xl border border-gray-200 z-[70] overflow-hidden max-h-[85vh]"
        style="bottom: calc(58px + env(safe-area-inset-bottom)); padding-top: env(safe-area-inset-top);"
        @click.stop
      >
        <!-- Modal Header -->
        <div class="flex items-center justify-between px-6 py-6 sticky top-[env(safe-area-inset-top)] bg-white z-50">
          <h2 class="text-xl font-medium">Budget Estimator</h2>
          <button @click="closeModal" class=" hover:text-gray-600 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <!-- Modal Body -->
        <div class="p-4 pb-0 overflow-y-auto flex-1">
          <!-- Intro text -->
          <div class="mb-6 leading-relaxed">
            <p class="text-lg text-center md:text-left">Tell us about your trip so we can estimate your travel budget.</p>
          </div>

          <!-- Location Component -->
          <div class="mb-3 relative z-[60]">
            <MultiLocation
              @select="handleLocationSelected"
            />
          </div>

          <!-- 3. When (Mobile Only) -->
          <div class="mb-5 md:hidden relative z-50" ref="dateContainerRef">
            <div class="flex flex-col gap-5">
              <!-- Quick Dates (hidden) -->
              <div class="hidden">
                <div class="grid grid-cols-2 gap-5 w-full">
                  <button
                    v-for="(quickDate, index) in quickDateOptions"
                    :key="quickDate.value"
                    @click="selectQuickDate(quickDate.value)"
                    :class="[
                      'w-full h-[50px] rounded-full text-lg font-semibold transition-all flex items-center justify-center px-1 whitespace-nowrap border',
                      selectedQuickDate === quickDate.value
                        ? 'bg-[#4ECF84] text-white border-[#4ECF84]'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
                      index === 2 ? 'col-span-2' : ''
                    ]"
                  >
                    {{ quickDate.label }}
                  </button>
                </div>
              </div>
              
              <!-- Calendar Trigger -->
              <button
                @click="openDatePicker"
                :class="[
                  'py-[14px] px-4 rounded-full text-lg font-semibold transition-all flex items-center gap-2 justify-between w-full border',
                  selectedDate?.startDate || selectedQuickDate || (tempDate?.tempStartDate && tempDate?.tempEndDate)
                    ? 'bg-white text-[#2F3033] border-[#2F3033]'
                    : 'bg-white text-gray-700 border-[#2F3033] hover:bg-gray-50'
                ]"
              >
                <span class="truncate">{{ dateHeaderText }}</span>
                <Icon name="calendar" class="w-5 h-5 flex-shrink-0" />
              </button>

              <!-- Calendar Dropdown -->
              <div 
                v-if="showDatePicker" 
                class="w-full bg-white border border-[#2F3033] rounded-3xl shadow-lg p-4 mt-2"
              >
                  <DropDownCalendar 
                    v-model="selectedDate"
                    :inline="true"
                    @dateSelected="handleDateSelected" 
                    @todaySelected="handleTodayQuickSelect"
                    @tempDateChanged="handleTempDateChanged"
                    :resetCalendarKey="resetCalendarKey" 
                  />
              </div>
            </div>
          </div>

          <!-- Travellers Accordion -->
          <div class="mb-4 relative z-40">
            <Travellers 
              ref="travellersMobileRef"
              v-model="travelers" 
              :is-mobile="true" 
              @dropdown-open="handleTravellersOpen"
            />

          </div>

          <!-- RefineTrip -->
          <div class="mt-5 mb-4">
            <BudgetEstimatorRefine
              v-model:open="showAdventure"
              :is-search-enabled="isSearchEnabled"
              v-model:budgetAmount="budgetAmount"
              v-model:travelersData="travelersData"
              :themeOptions="themeOptions"
              :travelMethodOptions="travelMethodOptions"
              :accommodationOptions="accommodationOptions"
              :specialRequirementsOptions="specialRequirementsOptions"
              v-model:selectedThemes="selectedThemes"
              v-model:selectedActivities="selectedActivities"
              v-model:selectedTravelMethods="selectedTravelMethods"
              v-model:selectedAccommodations="selectedAccommodations"
              v-model:selectedSpecialRequirements="selectedSpecialRequirements"
              @search="handleSearch"
              @validation-change="handleValidationChange"
            />
          </div>
        </div>

        <!-- Button Footer -->
        <!-- Error message -->
        <div v-if="errorMessage" class="px-4 pt-3 pb-0 bg-white z-[60] w-full mt-auto">
          <div class="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {{ errorMessage }}
          </div>
        </div>
        
        <div class="px-4 pb-8 pt-2 bg-white z-[60] w-full" :class="{ 'mt-auto': !errorMessage }">
          <button
            @click="handleSearch"
            :disabled="!isSearchEnabled || isLoading"
            :class="[
              'w-full px-6 py-3 rounded-full transition-colors text-lg font-semibold flex items-center justify-center',
              isSearchEnabled && !isLoading
                ? 'bg-[#4ECF84] text-white hover:bg-[#5bc49a] cursor-pointer'
                : 'bg-gray-50 text-gray-400 cursor-not-allowed'
            ]"
          >
            <span v-if="!isLoading">Calculate Budget</span>
            <span v-else class="flex items-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Calculating...
            </span>
          </button>
        </div>
      </div>
    </transition>
  </div>

  <!-- Desktop Only - Dropdown Modal -->
  <div class="hidden md:block">
    <!-- Backdrop Overlay for Desktop -->
    <transition name="backdrop-fade">
      <div
        v-if="showModal"
        class="fixed left-0 right-0 bottom-0 bg-transparent z-30"
        :style="desktopBackdropStyle"
        @click="closeModal"
      ></div>
    </transition>

    <!-- Desktop Dropdown Modal -->
    <transition name="fade-slide-down">
      <div
        v-if="showModal"
        ref="budgetEstimatorRef"
        class="hidden md:block fixed top-[56px] left-0 right-0 w-full bg-white shadow-md border-b border-gray-200 z-50"
        @click.stop
      >
        <!-- Close Button -->
        <button 
          @click="closeModal" 
          class="absolute top-1/2 -translate-y-1/2 right-6 z-[60] cursor-pointer text-[#2F3033] hover:text-black transition-colors bg-white rounded-full p-1 hover:bg-gray-100 shadow-sm"
          aria-label="Close Budget Estimator"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        
        <!-- Modal Body -->
        <div class="px-4 pt-4 pb-0 overflow-y-auto" style="max-height: 80vh;">          
          <!-- Single Row Layout for Desktop -->
          <div class="flex items-center justify-center gap-[10px] mb-4">

            <!-- Location Component -->
            <div class="flex-shrink-0">
              <MultiLocation 
                @select="handleLocationSelected" 
              />
            </div>

            <div class="flex-shrink-0 relative">
              <DropDownCalendar 
                ref="dropDownCalendarRef"
                v-model="selectedDate"
                :inline="false"
                @dateSelected="handleDateSelected" 
                @todaySelected="handleTodayQuickSelect"
                @tempDateChanged="handleTempDateChanged"
                @dropdown-open="handleCalendarOpen"
                :resetCalendarKey="resetCalendarKey"
                :externalDisplayText="desktopDateDisplayText"
              />
            </div>

            <!-- Travellers Component -->
            <div class="flex-shrink-0">
              <Travellers 
                ref="travellersDesktopRef"
                v-model="travelers" 
                :is-mobile="false"
                @dropdown-open="handleTravellersOpen"
              />
            </div>

            <!-- Refine Button -->
            <div class="flex-shrink-0">
              <button
                @click="toggleRefineBudget"
                :disabled="!isSearchEnabled"
                :class="[
                  'px-5 py-3 flex items-center transition-colors rounded-full shadow-sm',
                  !isSearchEnabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : (isRefineOpen ? 'bg-white border border-[#2F3033] text-[#4ECF84]' : 'bg-white border border-[#2F3033] text-gray-700')
                ]"
              >
                <Icon 
                  name="sliders-vertical"
                  class="w-4 h-4 mr-2"
                  :class="hasActiveFilters ? 'text-[#4ECF84]' : (isSearchEnabled ? 'text-[#2F3033]' : 'text-gray-400')"
                />
                <span class="text-base font-semibold">Refine Budget</span>
              </button>
            </div>

            <!-- Search Button -->
            <div class="flex-shrink-0">
              <button
                @click="handleSearch"
                :disabled="!isSearchEnabled || isLoading"
                :aria-label="isLoading ? 'Calculating' : 'Calculate Budget'"
                :class="[
                  'w-[48px] h-[48px] rounded-full flex items-center justify-center transition-colors',
                  isSearchEnabled && !isLoading
                    ? 'bg-[#4ECF84] text-white hover:bg-[#5bc49a] cursor-pointer'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                ]"
              >
                <Icon
                  v-if="!isLoading"
                  name="search"
                  class="w-5 h-5"
                />
                <svg
                  v-else
                  class="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </button>
            </div>

          </div>

          <!-- Backdrop Overlay -->
          <transition
            enter-active-class="transition-opacity duration-300 ease-out"
            leave-active-class="transition-opacity duration-200 ease-in"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >

            <div 
              v-if="isRefineOpen" 
              class="fixed inset-0 bg-[#2F3033]/30 backdrop-blur-sm z-[65]"
              @click="isRefineOpen = false"
            ></div>
          </transition>

          <!-- Desktop Accordion Panel - Positioned below Refine Trip button -->
          <transition
            name="accordion"
            enter-active-class="transition-all duration-300 ease-out"
            leave-active-class="transition-all duration-200 ease-in"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div 
              v-if="isRefineOpen" 
              class="absolute left-1/2 transform -translate-x-1/2 top-0 z-[70]"
              :style="refineDropdownStyle"
            >
              <BudgetEstimatorRefine 
                :show="false"
                :open="true"
                :is-search-enabled="isSearchEnabled"
                v-model:travelersData="travelersData"
                :themeOptions="themeOptions"
                :travelMethodOptions="travelMethodOptions"
                :accommodationOptions="accommodationOptions"
                :specialRequirementsOptions="specialRequirementsOptions"
                v-model:selectedThemes="selectedThemes"
                v-model:selectedActivities="selectedActivities"
                v-model:selectedTravelMethods="selectedTravelMethods"
                v-model:selectedAccommodations="selectedAccommodations"
                v-model:selectedSpecialRequirements="selectedSpecialRequirements"
                v-model:tripDirection="tripDirection"
                @search="handleSearch"
                @validation-change="handleValidationChange"
                @update:open="isRefineOpen = $event"
              />
            </div>
          </transition>
        </div><!-- /modal body px-4 -->
      </div><!-- /showModal div -->
    </transition><!-- /fade-slide-down -->
  </div><!-- /desktop block -->
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, watchEffect, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useSearchStore } from '../store/search.js'
import { useRouter } from 'vue-router'
import apiService from '@/services/api'
import { getDetectedCity, getDetectedCountry } from '@/services/utils/userLocation.js'
import MultiLocation from './MultiLocation.vue'

import DropDownCalendar from './DropDownCalendar.vue'
import BudgetEstimatorRefine from './BudgetEstimatorRefine.vue'
import Travellers from './Travellers.vue'
import Icon from './Icon.vue'
import { Calendar } from 'lucide-vue-next'

const router = useRouter()
const budgetEstimatorRef = ref(null)
const dropDownCalendarRef = ref(null)
const travellersMobileRef = ref(null)
const travellersDesktopRef = ref(null)
const tripDirection = ref('return')

// Props
const searchStore = useSearchStore()
const { travelers, dateRange } = storeToRefs(searchStore)
const props = defineProps({
  showModal: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'search'])

// Reactive data
const showAdventure = ref(false)
const isRefineOpen = ref(false) // Desktop refine accordion state
const showDateAccordion = ref(false) // mobile date accordion open/closed
const showDatePicker = ref(false) // Alternative mobile date picker toggle
const selectedQuickDate = ref('') 
const resetCalendarKey = ref(0) // Key to trigger calendar reset
const desktopModalTop = ref(72)
const dateContainerRef = ref(null)

// Quick date options
const quickDateOptions = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'next-weekend', label: 'Next Weekend' }
]

// Step-by-step selection state
// Step-by-step selection state
const selectedDate = dateRange
const tempDate = ref({ tempStartDate: null, tempEndDate: null }) // For real-time date display
const selectedDestination = ref(null) // Destination from location components
const budgetAmount = ref('')
const travelersData = travelers

// Theme selection state
const selectedThemes = ref([])

// Activity selection state
const selectedActivities = ref([])

// Travel method selection state
const selectedTravelMethods = ref([])

// Accommodation selection state
const selectedAccommodations = ref([])

// Special requirements selection state
const selectedSpecialRequirements = ref([])

// Theme options with placeholder icons
const themeOptions = [
  { id: 'adventure', label: 'Adventure', icon: '🏔️' },
  { id: 'nature', label: 'Nature', icon: '🌿' },
  { id: 'cultural', label: 'Cultural / Historical', icon: '🏛️' },
  { id: 'eco-friendly', label: 'Eco-friendly', icon: '♻️' },
  { id: 'wellbeing', label: 'Wellbeing', icon: '🧘' },
  { id: 'culinary', label: 'Culinary', icon: '🍽️' },
  { id: 'community', label: 'Community / Volunteer', icon: '🤝' },
  { id: 'creative', label: 'Creative', icon: '🎨' },
  { id: 'family', label: 'Family', icon: '👨‍👩‍👧‍👦' },
  { id: 'shop', label: 'Shop', icon: '🛍️' },
  { id: 'extreme-sports', label: 'Extreme Sports', icon: '🏄' }
]

// Travel method options with placeholder icons
const travelMethodOptions = [
  { id: 'car', label: 'Car', icon: '🚗' },
  { id: 'motorbike', label: 'Motorbike', icon: '🏍️' },
  { id: 'bicycle', label: 'Bicycle', icon: '🚴' },
  { id: 'campervan', label: 'Campervan', icon: '🚐' },
  { id: 'air', label: 'Air', icon: '✈️' },
  { id: 'train', label: 'Train', icon: '🚆' },
  { id: 'coach', label: 'Coach', icon: '🚌' },
  { id: 'yacht', label: 'Yacht', icon: '🛥️' },
  { id: 'cruise', label: 'Cruise', icon: '🚢' }
]

// Accommodation options with placeholder icons
const accommodationOptions = [
  { id: 'hotel', label: 'Hotel', icon: '🏨' },
  { id: 'motel', label: 'Motel', icon: '🏩' },
  { id: 'airbnb', label: 'Airbnb', icon: '🏠' },
  { id: 'homestay', label: 'Home stay', icon: '🏡' },
  { id: 'farmstay', label: 'Farm stay', icon: '🚜' },
  { id: 'eco-lodge', label: 'Eco-lodge', icon: '🌲' },
  { id: 'hostel', label: 'Hostel', icon: '🎒' },
  { id: 'boat', label: 'Boat', icon: '⛵' },
  { id: 'campsite', label: 'Campsite', icon: '⛺' },
  { id: 'cabin', label: 'Cabin', icon: '🏘️' }
]

// Special requirements options with placeholder icons
const specialRequirementsOptions = [
  { id: 'wheelchair-access', label: 'Wheelchair Access', icon: '♿' },
  { id: 'service-animal-friendly', label: 'Service Animal Friendly', icon: '🦮' },
  { id: 'pet-friendly', label: 'Pet Friendly', icon: '🐕' }
]

// Validation state from TripFinderRefine
const refineValidation = ref({
  needsTravellerValidation: false,
  isValidForSearch: true
})

// Travelers data for Travellers component
// Using 'travelers' directly from store


// Handle validation changes from TripFinderRefine
function handleValidationChange(validation) {
  refineValidation.value = validation
}

// Computed property to check if search should be enabled
const canSearch = computed(() => {
  // Budget estimator: require location, date, and at least one traveler
  const hasLocation = !!selectedDestination.value

  // Date must be a formally selected date (not just a temp selection in the calendar)
  const hasDate = !!(selectedDate.value?.startDate && selectedDate.value?.endDate)

  const hasTravelers = (travelersData.value.adults > 0) || (travelersData.value.children > 0) || (travelersData.value.pets > 0)

  return hasLocation && hasDate && hasTravelers
})

const isSearchEnabled = computed(() => {
  // Budget estimator requires: destination, date, and travelers
  return canSearch.value
})

watchEffect(() => {
  console.log('Mobile Check: ', {
    hasLocation: !!selectedDestination.value,
    hasDate: !!(selectedDate.value || (tempDate.value && tempDate.value.tempStartDate && tempDate.value.tempEndDate)),
    hasTravelers: (travelersData.value.adults > 0) || (travelersData.value.children > 0),
    isSearchEnabled: isSearchEnabled.value
  })
})

// Auto-close refine accordion if search becomes disabled (e.g., location/dates cleared)
watch(isSearchEnabled, (isEnabled) => {
  if (!isEnabled) {
    isRefineOpen.value = false // Desktop accordion
    showAdventure.value = false // Mobile accordion
  }
})

// Computed property to check if any filters are active in the Refine Budget section
const hasActiveFilters = computed(() => {
  return (
    budgetAmount.value !== '' ||
    selectedThemes.value.length > 0 ||
    selectedActivities.value.length > 0 ||
    selectedTravelMethods.value.length > 0 ||
    selectedAccommodations.value.length > 0 ||
    selectedSpecialRequirements.value.length > 0
  )
})



// Mobile body scroll lock handling
const bodyScrollY = ref(0)
const isBodyLocked = ref(false)
function isMobileScreen() {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
}

function lockBodyScroll() {
  if (isBodyLocked.value) return
  // Preserve current scroll position
  bodyScrollY.value = window.scrollY || window.pageYOffset || 0
  const body = document.body
  // Lock body to prevent background scroll while keeping visual position
  body.style.position = 'fixed'
  body.style.top = `-${bodyScrollY.value}px`
  body.style.left = '0'
  body.style.right = '0'
  body.style.width = '100%'
  body.style.overflow = 'hidden'
  body.classList.add('budgetestimator-modal-open')
  isBodyLocked.value = true
}

function unlockBodyScroll() {
  if (!isBodyLocked.value) return
  const body = document.body
  // Reset styles
  body.style.position = ''
  body.style.top = ''
  body.style.left = ''
  body.style.right = ''
  body.style.width = ''
  body.style.overflow = ''
  body.classList.remove('budgetestimator-modal-open')
  // Restore scroll position
  const y = bodyScrollY.value || 0
  window.scrollTo(0, y)
  isBodyLocked.value = false
}

function updateDesktopModalPosition() {
  if (typeof window === 'undefined') return
  const headerEl = document.querySelector('header')
  if (!headerEl) {
    desktopModalTop.value = 72
    return
  }
  const rect = headerEl.getBoundingClientRect()
  desktopModalTop.value = Math.max(0, Math.round(rect.bottom))
}

const desktopModalStyle = computed(() => {
  const topPx = `${desktopModalTop.value}px`
  return {
    top: topPx,
    maxHeight: `calc(100vh - ${topPx})`
  }
})

const desktopBackdropStyle = computed(() => {
  const topPx = `${desktopModalTop.value}px`
  return {
    top: topPx
  }
})



// Methods
function closeModal() {
  emit('close')
}



function handleLocationSelected(locationData) {
  console.log('Destination selected:', locationData)
  selectedDestination.value = locationData
}

function handleDateSelected(dateData) {
  console.log('Date selected:', dateData)
  selectedDate.value = dateData
  tempDate.value = { tempStartDate: null, tempEndDate: null } // Clear temp dates after applying
  // Close the mobile accordion/dropdown after applying selection
  showDateAccordion.value = false
  showDatePicker.value = false
}

function handleTempDateChanged(tempDateData) {
  console.log('Temp date changed:', tempDateData)
  tempDate.value = tempDateData
}

function clearDateSelection() {
  selectedDate.value = null
  tempDate.value = { tempStartDate: null, tempEndDate: null }
  resetCalendarKey.value += 1 // This will reset the calendar component
  console.log('Date selection cleared')
}

function handleTravellersOpen() {
  // Close desktop calendar if open
  if (dropDownCalendarRef.value) {
    dropDownCalendarRef.value.closeCalendar()
  }
  // Close mobile date accordion
  showDateAccordion.value = false
}

function handleCalendarOpen() {
  // Close travellers dropdowns if open
  if (travellersMobileRef.value) {
    travellersMobileRef.value.closeDropdown()
  }
  if (travellersDesktopRef.value) {
    travellersDesktopRef.value.closeDropdown()
  }
}

function openDatePicker() {
  showDatePicker.value = !showDatePicker.value
  selectedQuickDate.value = ''
  
  if (showDatePicker.value) {
    setTimeout(() => {
      if (dateContainerRef.value) {
        // Scroll the calendar container to the top so it's fully visible
        dateContainerRef.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100) // Small delay to allow DOM to render the calendar first
  }
}

function selectQuickDate(value) {
  selectedQuickDate.value = value
  selectedDate.value = null
  tempDate.value = { tempStartDate: null, tempEndDate: null }
  showDatePicker.value = false
  
  // Calculate dates based on quick selection
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  if (value === 'today') {
    selectedDate.value = { startDate: today, endDate: today }
  } else if (value === 'tomorrow') {
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    selectedDate.value = { startDate: tomorrow, endDate: tomorrow }
  } else if (value === 'next-weekend') {
    const daysUntilSaturday = (6 - today.getDay() + 7) % 7 || 7
    const saturday = new Date(today)
    saturday.setDate(saturday.getDate() + daysUntilSaturday)
    const sunday = new Date(saturday)
    sunday.setDate(sunday.getDate() + 1)
    selectedDate.value = { startDate: saturday, endDate: sunday }
  }
}

function handleDateAccordionToggle() {
  showDateAccordion.value = !showDateAccordion.value
  if (showDateAccordion.value) {
    // If opening date accordion, close travellers accordion
    if (travellersMobileRef.value) {
      travellersMobileRef.value.closeDropdown()
    }
  }
}

// Keep calendar field showing today's date when quick-select is used
function handleTodayQuickSelect() {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  selectedDate.value = { startDate: today, endDate: null }
  tempDate.value = { tempStartDate: null, tempEndDate: null } // Clear temp dates
  // Close the mobile accordion after quick select
  showDateAccordion.value = false
}

// Formatting utilities
function formatShort(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function toggleRefineBudget() {
  isRefineOpen.value = !isRefineOpen.value
  
  if (isRefineOpen.value) {
    // If opening refine, close travellers dropdown
    if (travellersDesktopRef.value) {
      travellersDesktopRef.value.closeDropdown()
    }
  }
}

const refineDropdownStyle = computed(() => {
  return {
    maxWidth: '96vw',
    width: '96vw'
  }
})

// Utility: get days count
const getDaysCount = (start, end) => {
  const msPerDay = 1000 * 60 * 60 * 24
  const days = Math.round((end.getTime() - start.getTime()) / msPerDay) + 1
  return `(${days} day${days > 1 ? 's' : ''})`
}

// Computed header text for mobile date accordion
const dateHeaderText = computed(() => {
  // Show real-time selection (temp dates) when available
  if (tempDate.value?.tempStartDate && tempDate.value?.tempEndDate) {
    return `${formatShort(tempDate.value.tempStartDate)} - ${formatShort(tempDate.value.tempEndDate)} ${getDaysCount(tempDate.value.tempStartDate, tempDate.value.tempEndDate)}`
  }
  if (tempDate.value?.tempStartDate) {
    return formatShort(tempDate.value.tempStartDate)
  }
  
  // Fall back to confirmed dates
  if (selectedDate.value?.startDate && selectedDate.value?.endDate) {
    return `${formatShort(selectedDate.value.startDate)} - ${formatShort(selectedDate.value.endDate)} ${getDaysCount(selectedDate.value.startDate, selectedDate.value.endDate)}`
  }
  if (selectedDate.value?.startDate) {
    return formatShort(selectedDate.value.startDate)
  }
  return 'Select trip dates'
})

// Desktop calendar display text
const desktopDateDisplayText = computed(() => {
  // Show real-time selection (temp dates) when available
  if (tempDate.value?.tempStartDate && tempDate.value?.tempEndDate) {
    return `${formatShort(tempDate.value.tempStartDate)} - ${formatShort(tempDate.value.tempEndDate)} ${getDaysCount(tempDate.value.tempStartDate, tempDate.value.tempEndDate)}`
  }
  if (tempDate.value?.tempStartDate) {
    return formatShort(tempDate.value.tempStartDate)
  }
  
  // Fall back to confirmed dates
  if (selectedDate.value?.startDate && selectedDate.value?.endDate) {
    return `${formatShort(selectedDate.value.startDate)} - ${formatShort(selectedDate.value.endDate)} ${getDaysCount(selectedDate.value.startDate, selectedDate.value.endDate)}`
  }
  if (selectedDate.value?.startDate) {
    return formatShort(selectedDate.value.startDate)
  }
  return 'Select trip dates'
})

// Loading and error states
const isLoading = ref(false)
const errorMessage = ref('')

async function handleSearch() {
  if (!isSearchEnabled.value) return

  // Clear any old budget data before starting new search
  localStorage.removeItem('budgetEstimationData')

  console.log('Budget calculation initiated with:', {
    scope: 'local',
    destination: selectedDestination.value,
    date: selectedDate.value,
    budget: budgetAmount.value,
    travelers: travelersData.value,
    themes: selectedThemes.value,
    activities: selectedActivities.value,
    travelMethods: selectedTravelMethods.value,
    accommodations: selectedAccommodations.value,
    specialRequirements: selectedSpecialRequirements.value
  })

  try {
    isLoading.value = true
    errorMessage.value = ''
    isRefineOpen.value = false
    showAdventure.value = false

    // Format dates to YYYY-MM-DD
    const formatDate = (date) => {
      if (!date) return null
      if (typeof date === 'string') return date
      if (date instanceof Date) {
        return date.toISOString().split('T')[0]
      }
      // Handle date object with startDate/endDate properties
      if (date.startDate) return formatDate(date.startDate)
      return null
    }

    // Get formatted dates
    const startDate = formatDate(selectedDate.value?.startDate || selectedDate.value)
    const endDate = formatDate(selectedDate.value?.endDate || selectedDate.value)

    // Validate required fields
    if (!startDate || !endDate) {
      errorMessage.value = 'Please select trip dates'
      isLoading.value = false
      return
    }

    if (!selectedDestination.value) {
      errorMessage.value = 'Please select a destination'
      isLoading.value = false
      return
    }

    // Extract plain string from location object
    const extractLocationString = (loc) => {
      if (!loc) return null
      if (typeof loc === 'string') return loc
      return loc.description || loc.name || loc.formatted_address || null
    }

    const destinationString = extractLocationString(selectedDestination.value)
    if (!destinationString) {
      errorMessage.value = 'Invalid destination selected. Please try again.'
      isLoading.value = false
      return
    }

    // Auto-detect origin from user location; fall back to a sensible default
    const detectedCity = getDetectedCity()
    const detectedCountry = getDetectedCountry()
    const originString = detectedCity
      ? `${detectedCity}, ${detectedCountry || 'Australia'}`
      : (detectedCountry || 'Sydney, Australia')

    // Map frontend travel method selections to backend transport_mode enum
    const transportModeMap = {
      air: 'flight', car: 'car', campervan: 'car', motorbike: 'car',
      train: 'train', coach: 'bus', bicycle: 'auto', yacht: 'auto', cruise: 'auto'
    }
    const transportMode = selectedTravelMethods.value?.length > 0
      ? (transportModeMap[selectedTravelMethods.value[0]] || 'auto')
      : 'auto'

    const adults = travelersData.value.adults || 0
    if (adults < 1) {
      errorMessage.value = 'At least 1 adult is required to estimate a trip.'
      isLoading.value = false
      return
    }

    const requestData = {
      destination: destinationString,
      origin: originString,
      start_date: startDate,
      end_date: endDate,
      adults,
      children: travelersData.value.children || 0,
      pets: travelersData.value.pets || 0,
      holiday_types: selectedThemes.value || [],
      accommodation_preference: selectedAccommodations.value?.[0] || 'mid-range',
      activity_level: selectedActivities.value?.length > 3 ? 'high'
        : selectedActivities.value?.length > 0 ? 'moderate' : 'low',
      transport_mode: transportMode,
      trip_direction: tripDirection.value || 'return'
    }

    console.log('Calling budget API with:', requestData)

    // Call real API (note: apiService.getBudgetEstimation, not apiService.budget.getBudgetEstimation)
    const budgetEstimate = await apiService.getBudgetEstimation(requestData)

    console.log('Budget API response:', budgetEstimate)

    // Store both API response AND request data in localStorage for BudgetEstimation page
    const dataToStore = {
      ...budgetEstimate,
      request: requestData // Include original request for dates and other info
    }
    // Set version key first so BudgetEstimation.vue's cache check doesn't delete our fresh data
    localStorage.setItem('budgetEstimationVersion', 'v2')
    localStorage.setItem('budgetEstimationData', JSON.stringify(dataToStore))

    // Navigate to results page
    router.push('/budget-estimator')

    // Close modal
    emit('close')

  } catch (error) {
    console.error('Budget estimation failed:', error)
    errorMessage.value = error.response?.data?.detail || 'Failed to calculate budget. Please try again.'
  } finally {
    isLoading.value = false
  }
}



onMounted(() => {
  window.addEventListener('resize', updateDesktopModalPosition)
  nextTick(() => updateDesktopModalPosition())
  // If modal is initially open on mobile, ensure body is locked
  if (props.showModal && isMobileScreen()) {
    lockBodyScroll()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateDesktopModalPosition)
  // Ensure body is unlocked when component unmounts
  unlockBodyScroll()
})

watch(() => props.showModal, async (open) => {
  if (open) {
    await nextTick()
    updateDesktopModalPosition()
    // Lock background scroll on mobile when modal opens
    if (isMobileScreen()) {
      lockBodyScroll()
    }
  } else {
    // Unlock when modal closes
    unlockBodyScroll()
  }
})

// Handle escape key for modal
const handleEscape = (event) => {
  if (event.key === 'Escape' && props.showModal) {
    closeModal()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
/* Slide-up Modal Animations */
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-enter-active {
  transition: transform 250ms ease-out, opacity 250ms ease-out;
}

.slide-up-leave-active {
  transition: transform 200ms ease-in, opacity 200ms ease-in;
}

.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateY(0);
  opacity: 1;
}

/* Backdrop Fade Animations */
.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
  opacity: 0;
}

.backdrop-fade-enter-active {
  transition: opacity 250ms ease-out;
}

.backdrop-fade-leave-active {
  transition: opacity 200ms ease-in;
}

.backdrop-fade-enter-to,
.backdrop-fade-leave-from {
  opacity: 1;
}

/* Desktop Fade + Slide-down Animations */
.fade-slide-down-enter-from,
.fade-slide-down-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

.fade-slide-down-enter-active {
  transition: transform 250ms ease-out, opacity 250ms ease-out;
}

.fade-slide-down-leave-active {
  transition: transform 200ms ease-in, opacity 200ms ease-in;
}

.fade-slide-down-enter-to,
.fade-slide-down-leave-from {
  transform: translateY(0);
  opacity: 1;
}

/* Basic modal styles */
button:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease-in-out;
}

button:active {
  transform: translateY(0);
}

/* Focus styles for accessibility */
button:focus {
  outline: none;
  outline-offset: 2px;
}

/* Custom scrollbar for mobile modal */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Calendar day hover effects */
.aspect-square:hover:not(:disabled) {
  transform: scale(1.05);
}

/* Smooth transitions for accordions */
.transition-transform {
  transition: transform 0.2s ease-in-out;
}

/* Mobile input styling to prevent zoom on iOS */
input[type="date"],
input[type="text"],
input[type="number"] {
  font-size: 16px;
  -webkit-appearance: none;
  appearance: none;
}

/* Desktop dropdown positioning */
.relative .absolute {
  z-index: 50;
}

/* Higher z-index for When and Travellers dropdowns to overlay preferences */
.relative .absolute.when-dropdown,
.relative .absolute.travellers-dropdown {
  z-index: 60;
}

/* Ensure proper spacing for desktop layout */
@media (min-width: 768px) {
  .flex-wrap {
    flex-wrap: wrap;
  }
  
  .flex-1 {
    min-width: 200px;
  }
}

/* Mobile-specific adjustments */
@media (max-width: 767px) {
  .fixed.inset-0 {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
  
  .rounded-t-3xl {
    border-top-left-radius: 1.5rem;
    border-top-right-radius: 1.5rem;
  }
}

/* Toggle switch animations */
.relative.inline-flex span {
  transition: transform 0.3s ease-in-out;
}

/* Accordion content animations */
.space-y-4 > div {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Custom styling for counter buttons */
.w-8.h-8 {
  min-width: 2rem;
  min-height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Ensure proper text alignment in counters */
.w-8.text-center {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
}

/* Shadow effects for desktop dropdowns */
.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Responsive grid adjustments */
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

/* Ensure proper spacing in mobile layout */
.space-y-6 > * + * {
  margin-top: 1.5rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.space-y-3 > * + * {
  margin-top: 0.75rem;
}

/* Custom focus ring for toggle switches */
.focus\:ring-2:focus {
  --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color);
  box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
}

/* Ensure proper color transitions */
.transition-colors {
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* Prevent text selection on buttons */
button {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* Ensure proper backdrop blur for modal */
.bg-black.bg-opacity-50 {
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}
</style>
